import 'zod/compile';
import { z } from 'zod';
import type { Node, Edge } from '@xyflow/react';
import type { StepNode } from './components/config-renderers';
import { IOType } from '@erp/shared-types/node-graph';
import { type UndoTree, trimUndoTree } from '@/lib/undoTree';
import { parseUndoTree } from '@/lib/undoTreeSchema';
import { decodeShared, encodeShared } from '@/lib/sharedJson';
import { STEP_LAYOUT_GROUP_NODE_TYPE } from '@/lib/workflows/stepLayoutGroups';
import { ZipTieSchema, type ZipTie } from '@erp/workflows';

export type ZipTieAction = 'bundle' | 'move' | 'rotate' | 'remove' | 'recolor';

const socketSchema = z.object({
  id: z.string(),
  name: z.string(),
  ioType: z.enum(IOType),
  socketType: z.string().optional(),
  required: z.boolean().optional(),
});
const nodeSchema = z.object({
  id: z.string(),
  type: z.string().optional(),
  position: z.object({ x: z.number(), y: z.number() }),
  parentId: z.string().optional(),
  style: z
    .object({
      width: z.union([z.number(), z.string()]).optional(),
      height: z.union([z.number(), z.string()]).optional(),
      pointerEvents: z.literal('none').optional(),
    })
    .optional(),
  dragHandle: z.string().optional(),
  data: z.object({
    label: z.string(),
    stepType: z.string().optional(),
    category: z.string().optional(),
    config: z.record(z.string(), z.unknown()).optional(),
    inputs: z.record(z.string(), socketSchema),
    outputs: z.record(z.string(), socketSchema),
    emits: z.array(z.string()).optional(),
    groupId: z.string().optional(),
    color: z.string().nullable().optional(),
    workflowId: z.string().optional(),
    __socketsRefreshed: z.boolean().optional(),
  }),
});
const edgeSchema = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  sourceHandle: z.string().nullable().optional(),
  targetHandle: z.string().nullable().optional(),
  type: z.string().optional(),
  animated: z.boolean().optional(),
});
export const workflowGraphSnapshotSchema = z.object({
  nodes: z.array(nodeSchema),
  edges: z.array(edgeSchema),
  zipTies: z.array(ZipTieSchema).prefault([]),
});
export const workflowSnapshotSchema = workflowGraphSnapshotSchema.extend({
  workflowName: z.string(),
  workflowDescription: z.string(),
  workflowNamespaceId: z.string().nullable(),
  orchestratorType: z.enum(['temporal', 'internal_rabbitmq']),
});
export type WorkflowSnapshot = Omit<
  z.infer<typeof workflowSnapshotSchema>,
  'nodes' | 'edges'
> & { nodes: StepNode[]; edges: Edge[] };
export type GraphSnapshot = {
  nodes: Node[];
  edges: Edge[];
  zipTies?: ZipTie[];
};

const NODE_DATA_FIELDS = new Set([
  'label',
  'stepType',
  'category',
  'config',
  'inputs',
  'outputs',
  'emits',
  'groupId',
  'color',
  'workflowId',
  '__socketsRefreshed',
]);

/** Canvas selection, measurement, registry hydration and decorations are not edits. */
export function historySnapshot<S extends GraphSnapshot>(snapshot: S): S {
  return {
    ...snapshot,
    nodes: snapshot.nodes.map((node) => {
      const data = { ...node.data };
      for (const key of Object.keys(data))
        if (!NODE_DATA_FIELDS.has(key)) delete data[key];
      return {
        id: node.id,
        type: node.type,
        position: node.position,
        parentId: node.parentId,
        dragHandle: node.dragHandle,
        ...(node.type === STEP_LAYOUT_GROUP_NODE_TYPE
          ? {
              style: {
                width: node.style?.width,
                height: node.style?.height,
                pointerEvents: 'none',
              },
            }
          : {}),
        data,
      };
    }),
    edges: snapshot.edges.map(
      ({ id, source, target, sourceHandle, targetHandle, type, animated }) => ({
        id,
        source,
        target,
        sourceHandle,
        targetHandle,
        type,
        animated,
      }),
    ),
    zipTies: snapshot.zipTies ?? [],
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
export function sameHistoryValue(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (Array.isArray(a) && Array.isArray(b))
    return (
      a.length === b.length &&
      a.every((value, index) => sameHistoryValue(value, b[index]))
    );
  if (!isRecord(a) || !isRecord(b)) return false;
  const keys = Object.keys(a).filter((key) => a[key] !== undefined);
  return (
    keys.length ===
      Object.keys(b).filter((key) => b[key] !== undefined).length &&
    keys.every((key) => sameHistoryValue(a[key], b[key]))
  );
}
export function sameHistorySnapshot(
  a: GraphSnapshot,
  b: GraphSnapshot,
): boolean {
  return sameHistoryValue(historySnapshot(a), historySnapshot(b));
}

export type WorkflowHistoryIntent =
  | { kind: 'graph' }
  | { kind: 'layout-group' }
  | { kind: 'elsewhere' | 'reset' | 'import' | 'auto-layout' }
  | { kind: 'step-data'; action: 'sockets' | 'events' | 'type'; nodeId: string }
  | { kind: 'add'; name: string }
  | { kind: 'layout'; action: 'add'; name: string }
  | { kind: 'layout'; action: 'remove' | 'resize' | 'color'; nodeId: string }
  | { kind: 'layout'; action: 'rename'; nodeId: string; name: string }
  | {
      kind:
        | 'duplicate'
        | 'remove'
        | 'config'
        | 'refresh'
        | 'move'
        | 'expression'
        | 'disconnect-node';
      nodeId: string;
    }
  | { kind: 'rename'; nodeId: string; name: string }
  | { kind: 'connect'; source: string; target: string }
  | { kind: 'disconnect'; edgeIds: string[] }
  | {
      kind: 'zip-tie';
      action: ZipTieAction;
    }
  | {
      kind: 'metadata';
      field:
        | 'workflowName'
        | 'workflowDescription'
        | 'workflowNamespaceId'
        | 'orchestratorType';
    };

/** Receives gt() in the editor; tests use an identity translator. */
export type HistoryTranslate = (
  text: string,
  options: { context: string; [key: string]: string },
) => string;
const nameOf = (snapshot: GraphSnapshot, id: string) => {
  const label = snapshot.nodes.find((node) => node.id === id)?.data.label;
  return typeof label === 'string' && label !== '' ? label : id;
};

function layoutNodeHistoryChange(
  old: Node,
  node: Node,
): WorkflowHistoryIntent | null {
  if (old.type !== STEP_LAYOUT_GROUP_NODE_TYPE) return null;
  if (old.data.label !== node.data.label)
    return {
      kind: 'layout',
      action: 'rename',
      nodeId: old.id,
      name: String(node.data.label),
    };
  if (old.data.color !== node.data.color)
    return { kind: 'layout', action: 'color', nodeId: old.id };
  if (!sameHistoryValue(old.style, node.style))
    return { kind: 'layout', action: 'resize', nodeId: old.id };
  return null;
}

function stepDataHistoryChange(
  old: Node,
  node: Node,
): WorkflowHistoryIntent | null {
  if (old.data.stepType !== node.data.stepType)
    return { kind: 'step-data', action: 'type', nodeId: old.id };
  if (!sameHistoryValue(old.data.emits, node.data.emits))
    return { kind: 'step-data', action: 'events', nodeId: old.id };
  if (
    !sameHistoryValue(old.data.inputs, node.data.inputs) ||
    !sameHistoryValue(old.data.outputs, node.data.outputs)
  )
    return { kind: 'step-data', action: 'sockets', nodeId: old.id };
  return null;
}

function nodeHistoryChange(
  old: Node,
  node: Node,
): WorkflowHistoryIntent | null {
  const layout = layoutNodeHistoryChange(old, node);
  if (layout) return layout;
  if (old.data.label !== node.data.label)
    return { kind: 'rename', nodeId: old.id, name: String(node.data.label) };
  if (!sameHistoryValue(old.data.config, node.data.config))
    return { kind: 'config', nodeId: old.id };
  if (
    !sameHistoryValue(old.position, node.position) ||
    old.parentId !== node.parentId
  )
    return { kind: 'move', nodeId: old.id };
  return stepDataHistoryChange(old, node);
}

function describeGraph(
  base: GraphSnapshot,
  next: GraphSnapshot,
): WorkflowHistoryIntent[] {
  const before = new Map(base.nodes.map((node) => [node.id, node]));
  const after = new Map(next.nodes.map((node) => [node.id, node]));
  const added: WorkflowHistoryIntent[] = next.nodes.flatMap(
    (node): WorkflowHistoryIntent[] =>
      !before.has(node.id)
        ? [
            node.type === STEP_LAYOUT_GROUP_NODE_TYPE
              ? { kind: 'layout', action: 'add', name: String(node.data.label) }
              : { kind: 'add', name: String(node.data.label) },
          ]
        : [],
  );
  const removed: WorkflowHistoryIntent[] = base.nodes.flatMap(
    (node): WorkflowHistoryIntent[] =>
      !after.has(node.id)
        ? [
            node.type === STEP_LAYOUT_GROUP_NODE_TYPE
              ? { kind: 'layout', action: 'remove', nodeId: node.id }
              : { kind: 'remove', nodeId: node.id },
          ]
        : [],
  );
  if (added.length || removed.length) return [...added, ...removed];
  const addedEdges = next.edges.filter(
    (edge) => !base.edges.some((old) => old.id === edge.id),
  );
  if (addedEdges.length)
    return addedEdges.map((edge) => ({
      kind: 'connect',
      source: edge.source,
      target: edge.target,
    }));
  const removedEdges = base.edges.filter(
    (old) => !next.edges.some((edge) => edge.id === old.id),
  );
  if (removedEdges.length)
    return [
      { kind: 'disconnect', edgeIds: removedEdges.map((edge) => edge.id) },
    ];
  return base.nodes.flatMap((old) => {
    const node = after.get(old.id);
    const change = node ? nodeHistoryChange(old, node) : null;
    return change ? [change] : [];
  });
}

function layoutHistoryStep(
  base: GraphSnapshot,
  intent: Extract<WorkflowHistoryIntent, { kind: 'layout' }>,
  gt: HistoryTranslate,
): { label: string; group?: string } {
  const context = 'workflow editor edit history';
  if (intent.action === 'add')
    return {
      label: gt('Added layout group ‘{name}’', { context, name: intent.name }),
    };
  const name = nameOf(base, intent.nodeId);
  switch (intent.action) {
    case 'remove':
      return { label: gt('Removed layout group ‘{name}’', { context, name }) };
    case 'resize':
      return { label: gt('Resized layout group ‘{name}’', { context, name }) };
    case 'color':
      return {
        label: gt('Changed color of layout group ‘{name}’', { context, name }),
      };
    case 'rename':
      return {
        label: gt('Renamed layout group ‘{old}’ → ‘{name}’', {
          context,
          old: name,
          name: intent.name,
        }),
        group: `rename:${intent.nodeId}`,
      };
  }
}

function historyWorkflowName(snapshot: GraphSnapshot): string {
  return 'workflowName' in snapshot && typeof snapshot.workflowName === 'string'
    ? snapshot.workflowName
    : '';
}

function metadataHistoryStep(
  base: GraphSnapshot,
  next: GraphSnapshot,
  field: Extract<WorkflowHistoryIntent, { kind: 'metadata' }>['field'],
  gt: HistoryTranslate,
): { label: string; group: string } {
  const context = 'workflow editor edit history';
  const labels = {
    workflowName: gt('Renamed workflow ‘{old}’ → ‘{name}’', {
      context,
      old: historyWorkflowName(base),
      name: historyWorkflowName(next),
    }),
    workflowDescription: gt('Changed workflow description', { context }),
    workflowNamespaceId: gt('Changed workflow namespace', { context }),
    orchestratorType: gt('Changed workflow orchestrator', { context }),
  };
  return { label: labels[field], group: `metadata:${field}` };
}

function graphHistoryStep(
  base: GraphSnapshot,
  next: GraphSnapshot,
  kind: 'graph' | 'layout-group',
  gt: HistoryTranslate,
): { label: string; group?: string } {
  const changes = describeGraph(base, next);
  if (changes.length === 1 && changes[0])
    return workflowHistoryStep(base, next, changes[0], gt);
  if (changes.length > 1)
    return {
      label: changes
        .map((change) => workflowHistoryStep(base, next, change, gt).label)
        .join('; '),
    };
  const context = 'workflow editor edit history';
  return {
    label:
      kind === 'layout-group'
        ? gt('Changed layout groups', { context })
        : gt('Changed workflow graph', { context }),
  };
}

function disconnectHistoryStep(
  base: GraphSnapshot,
  edgeIds: string[],
  gt: HistoryTranslate,
): { label: string } {
  const context = 'workflow editor edit history';
  const disconnectedIds = new Set(edgeIds);
  const labels = base.edges.flatMap((edge) =>
    disconnectedIds.has(edge.id)
      ? [
          gt('Disconnected ‘{source}’ → ‘{target}’', {
            context,
            source: nameOf(base, edge.source),
            target: nameOf(base, edge.target),
          }),
        ]
      : [],
  );
  return { label: labels.join('; ') || gt('Disconnected steps', { context }) };
}

function disconnectedNodeEdgeIds(
  base: GraphSnapshot,
  nodeId: string,
): string[] {
  return base.edges.flatMap((edge) =>
    edge.source === nodeId || edge.target === nodeId ? [edge.id] : [],
  );
}

function stepDataHistoryStep(
  base: GraphSnapshot,
  intent: Extract<WorkflowHistoryIntent, { kind: 'step-data' }>,
  gt: HistoryTranslate,
): { label: string } {
  const context = 'workflow editor edit history';
  const name = nameOf(base, intent.nodeId);
  const labels = {
    sockets: gt('Changed sockets of ‘{name}’', { context, name }),
    events: gt('Changed emitted events of ‘{name}’', { context, name }),
    type: gt('Changed type of ‘{name}’', { context, name }),
  };
  return { label: labels[intent.action] };
}

export function workflowHistoryStep(
  base: GraphSnapshot,
  next: GraphSnapshot,
  intent: WorkflowHistoryIntent,
  gt: HistoryTranslate,
): { label: string; group?: string } {
  if (intent.kind === 'graph' || intent.kind === 'layout-group')
    return graphHistoryStep(base, next, intent.kind, gt);
  return workflowHistoryIntentStep(base, next, intent, gt);
}

export function workflowHistoryIntentStep(
  base: GraphSnapshot,
  next: GraphSnapshot,
  intent: Exclude<WorkflowHistoryIntent, { kind: 'graph' | 'layout-group' }>,
  gt: HistoryTranslate,
): { label: string; group?: string } {
  const context = 'workflow editor edit history';
  switch (intent.kind) {
    case 'step-data':
      return stepDataHistoryStep(base, intent, gt);
    case 'add':
      return {
        label: gt('Added step ‘{name}’', { context, name: intent.name }),
      };
    case 'duplicate':
      return {
        label: gt('Duplicated step ‘{name}’', {
          context,
          name: nameOf(base, intent.nodeId),
        }),
      };
    case 'remove':
      return {
        label: gt('Removed step ‘{name}’', {
          context,
          name: nameOf(base, intent.nodeId),
        }),
      };
    case 'refresh':
      return {
        label: gt('Refreshed sockets of ‘{name}’', {
          context,
          name: nameOf(base, intent.nodeId),
        }),
      };
    case 'config':
      return {
        label: gt('Changed config of ‘{name}’', {
          context,
          name: nameOf(base, intent.nodeId),
        }),
        group: `config:${intent.nodeId}`,
      };
    case 'expression':
      return {
        label: gt('Changed expression overrides of ‘{name}’', {
          context,
          name: nameOf(base, intent.nodeId),
        }),
        group: `expression:${intent.nodeId}`,
      };
    case 'move':
      return {
        label: gt('Moved ‘{name}’', {
          context,
          name: nameOf(base, intent.nodeId),
        }),
        group: `move:${intent.nodeId}`,
      };
    case 'rename':
      return {
        label: gt('Renamed ‘{old}’ → ‘{name}’', {
          context,
          old: nameOf(base, intent.nodeId),
          name: intent.name,
        }),
        group: `rename:${intent.nodeId}`,
      };
    case 'connect':
      return {
        label: gt('Connected ‘{source}’ → ‘{target}’', {
          context,
          source: nameOf(base, intent.source),
          target: nameOf(base, intent.target),
        }),
      };
    case 'disconnect':
      return disconnectHistoryStep(base, intent.edgeIds, gt);
    case 'disconnect-node':
      return disconnectHistoryStep(
        base,
        disconnectedNodeEdgeIds(base, intent.nodeId),
        gt,
      );
    case 'metadata':
      return metadataHistoryStep(base, next, intent.field, gt);
    case 'layout':
      return layoutHistoryStep(base, intent, gt);
    case 'reset':
      return { label: gt('Reset to saved draft', { context }) };
    case 'import':
      return { label: gt('Imported workflow', { context }) };
    case 'auto-layout':
      return { label: gt('Auto-laid out workflow', { context }) };
    case 'elsewhere':
      return { label: gt('Changed elsewhere', { context }) };
    case 'zip-tie':
      return zipTieHistoryStep(intent.action, gt);
  }
}

function zipTieHistoryStep(
  action: ZipTieAction,
  gt: HistoryTranslate,
): { label: string } {
  const context = 'workflow editor edit history';
  const labels: Record<typeof action, string> = {
    bundle: gt('Zip-tied connections', { context }),
    move: gt('Moved zip tie', { context }),
    rotate: gt('Rotated zip tie', { context }),
    remove: gt('Removed zip tie', { context }),
    recolor: gt('Recolored zip tie', { context }),
  };
  return { label: labels[action] };
}

export const WORKFLOW_HISTORY_MAX_NODES = 100;
const historyKey = (draftId: string, surface: string) =>
  `workflow:${draftId}:history:${surface}`;
export function clearWorkflowEditorHistory(draftId: string): void {
  try {
    for (const surface of ['editor', 'group'])
      sessionStorage.removeItem(historyKey(draftId, surface));
  } catch {
    /* Storage is unavailable. */
  }
}

export function saveWorkflowEditorHistory<S extends GraphSnapshot>(
  draftId: string,
  tree: UndoTree<S>,
  saved: S | null,
  surface = 'editor',
): void {
  const key = historyKey(draftId, surface);
  let size = Math.min(tree.nodes.length, WORKFLOW_HISTORY_MAX_NODES);
  for (;;) {
    try {
      sessionStorage.setItem(
        key,
        JSON.stringify({
          format: 1,
          saved,
          tree: encodeShared(trimUndoTree(tree, size)),
        }),
      );
      return;
    } catch {
      try {
        sessionStorage.removeItem(key);
      } catch {
        return;
      }
      if (size === 1) return;
      size = Math.max(1, Math.floor(size / 2));
    }
  }
}

export function loadWorkflowEditorHistory<S extends GraphSnapshot>(
  draftId: string,
  baseline: S,
  schema: z.ZodType<NoInfer<S>>,
  surface = 'editor',
): { tree: UndoTree<S>; saved: S | null } | null {
  try {
    const raw = sessionStorage.getItem(historyKey(draftId, surface));
    if (raw === null) return null;
    const parsed = z
      .object({
        format: z.literal(1),
        saved: schema.nullable(),
        tree: z.unknown(),
      })
      .safeParse(JSON.parse(raw));
    if (
      !parsed.success ||
      parsed.data.saved === null ||
      !sameHistorySnapshot(parsed.data.saved, baseline)
    )
      return null;
    const tree = parseUndoTree(decodeShared(parsed.data.tree), schema);
    if (!tree || tree.nodes.length > WORKFLOW_HISTORY_MAX_NODES) return null;
    const validParents = tree.nodes.every(
      (node) => node.parent === null || node.parent >= 0,
    );
    const validRedo = Object.entries(tree.redoTo).every(
      ([from, to]) => tree.nodes[to]?.parent === Number(from),
    );
    return validParents && validRedo
      ? { tree, saved: parsed.data.saved }
      : null;
  } catch {
    return null;
  }
}

export function historyFrameContains(
  frame: Node,
  position: { x: number; y: number },
  defaults: { width: number; height: number },
): boolean {
  const width =
    frame.width ??
    (typeof frame.style?.width === 'number'
      ? frame.style.width
      : defaults.width);
  const height =
    frame.height ??
    (typeof frame.style?.height === 'number'
      ? frame.style.height
      : defaults.height);
  return (
    position.x >= frame.position.x &&
    position.x <= frame.position.x + width &&
    position.y >= frame.position.y &&
    position.y <= frame.position.y + height
  );
}
